import { Map } from 'maplibre-gl';
import { FeatureCollection } from "geojson";
import { LayerParams } from "../../map/layer-manager";
import { PaintStrategy } from "../../utils/paint-strategy";
import { interpolateRain } from "../../utils/rain-interpolator";
import { MapLayer } from "../map-layer";
import { UnsetColor } from "../unset-color";

/**
 * Camada de linhas, p.ex. vias e rios.
 */
export class LineLayer extends MapLayer implements UnsetColor {

  constructor(private sourceId: string, private layerId: string) {
    super();
  }

  hasData(layerParams: LayerParams): boolean {
    let { segmentsData } = layerParams;
    return segmentsData != null && segmentsData.features.length > 0;
  }

  /**
   * Define a fonte de dados (data source) da camada
   * 
   * @param map instância do mapa mapboxgl
   * @param layerParams parâmetros da camada, que inclui segmentsData
   */
  setSource(map: Map, layerParams: LayerParams) {

    let { segmentsData } = layerParams;

    map.addSource(this.sourceId, {
      type: 'geojson',
      data: segmentsData,
      promoteId: 'ogc_fid'
    });
  }

  remove(map: Map) {

    if (map.getLayer(this.layerId)) {
      map.removeLayer(this.layerId);
    }

    // if (map.getSource(this.sourceId)) {
    //   map.removeSource(this.sourceId);
    // }
  }

  /**
   * Exibe a camada de linhas, podendo ser vias ou rios.
   * @param layerParams parâmetros de exibição da camada.
   */
  show(map: Map, layerParams: LayerParams) {
    
    let { rainData, p, selectedTimestamp, segmentsData, } = layerParams;

    if (rainData == null || rainData.features.length == 0) {
      this.setMapLineLayer(map, '#808080', 1, 1);
      return;
    }
    
    // calcular a interpolação da chuva das estações nas vias.
    segmentsData.features = interpolateRain(
      rainData, segmentsData.features, p, selectedTimestamp
    );
    
    // definir a cor usando o esquema contínuo, discreto ou constante.
    const painter = PaintStrategy.factory(layerParams);

    segmentsData.features.forEach(feature => {
      let { rain_value } = feature.properties;
      feature.properties.color = painter(rain_value);
    });

    // reiniciar a layer das vias
    this.loadSegmentLayer(map);

    // adicionar as cores dos segmentos na data source das vias 
    segmentsData.features.forEach(elem => {
      
      map.setFeatureState(
        {
          source: this.sourceId,
          id: elem.properties.ogc_fid,
        }, 
        {
          rainColor: elem.properties.color,
          rainHeatmap: true,
          rainValue: elem.properties.rain_value
        }
      );

    });
  }

  unsetColor(map: Map, layerParams: LayerParams) {

    let { segmentsData } = layerParams;

    segmentsData.features.forEach(elem => {
      elem.properties.color = '#808080';
    });

    segmentsData.features.forEach(elem => {
      
      map.setFeatureState(
        {
          source: this.sourceId,
          id: elem.properties.ogc_fid,
        }, 
        {
          rainColor: elem.properties.color,
          rainHeatmap: true,
          rainValue: elem.properties.rain_value
        }
      );

    });
  }


  /**
   * Set mapbox instance line layer
   * @see https://docs.mapbox.com/mapbox-gl-js/example/geojson-polygon/
   * @param map mapbox instance which will be configured
   * @param dataSourceId data source exclusive identifier
   * @param dataLayerLineId line type layer exclusive identifier
   * @param lineColor layer line color
   * @param lineOpacity layer line opacity
   * @param lineWidth layer line width
   */
  setMapLineLayer(map: Map,
    lineColor: string, lineOpacity: number, lineWidth: number) {
    // lastly, we add a type outline layer to draw the boundaries of the reigions

    map.addLayer({
      id: this.layerId,
      type: 'line',
      source: this.sourceId,
      layout: {
        visibility: 'visible',
      },
      paint: {
        'line-color': lineColor,
        'line-opacity': 1,
        'line-width': lineWidth,
      },
    });
  }

  /**
   * Carrega a layer de linhas com lógica para colorir as vias de acordo com os níveis de chuva.
   * Esses níveis de chuva das vias são carregados posteriormente.
   * 
   * @param segments_url url de busca das vias
   * @param lineSourceId id da data-source das vias
   * @param lineLayerId id da layer das linhas das vias
   */
  loadSegmentLayer(map: Map) {

    // Colored Segment layer
    map.addLayer({
      'id': this.layerId,
      'type': 'line',
      'source': this.sourceId,
      'paint': {
        'line-color': [
          'case',
          ['!=', ['feature-state', 'rainHeatmap'], null], //if
          ['feature-state', 'rainColor'], //then
          'white' //else
        ],
        'line-width': 1
      }
    });
  }
  
}
